import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import logo from '../../../img/logobranco.png'; // Importando o logo
import dashboardImage from '../../../videos/dashboard.png'; // Importando a imagem do dashboard
import analyticsVideo from '../../../videos/analytics.mp4'; // Importando o vídeo de analytics
import buscaPremiumVideo from '../../../videos/busca-premium.mp4'; // Importando o vídeo de busca premium
import telegramBotVideo from '../../../videos/telegram-bot.mp4'; // Importando o vídeo do Telegram Bot

// Componente para o gráfico de fundo estilizado
const BackgroundChart: React.FC = () => (
  <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: 'rgba(29, 78, 216, 0.1)' }} />
          <stop offset="100%" style={{ stopColor: 'rgba(29, 78, 216, 0)' }} />
        </linearGradient>
      </defs>
      <path
        d="M0,150 Q200,50 400,120 T800,150 T1200,100 T1600,180 L1600,0 L0,0 Z"
        fill="url(#chartGradient)"
        transform="translate(0, 200) scale(1.5)"
      />
      <path
        d="M-200,200 Q0,250 200,180 T600,220 T1000,160 T1400,250 T1800,200"
        stroke="#36ca9b"
        strokeWidth="3"
        fill="none"
        strokeDasharray="10 5"
        opacity="0.2"
        transform="translate(0, 50)"
      />
    </svg>
  </div>
);


const LandingPage: React.FC = () => {
  const auth = useAuth();

  const handleSignupClick = async () => {
    try {
      await auth.signinRedirect({
        extraQueryParams: {
          screen_hint: 'sign_up'
        }
      });
    } catch (error) {
      console.error('Erro ao redirecionar para cadastro:', error);
    }
  };

  const handleLoginClick = async () => {
    try {
      await auth.signinRedirect();
    } catch (error) {
      console.error('Erro ao redirecionar para login:', error);
    }
  };

  return (
    <div className="bg-accent text-dark font-sans relative">
      <BackgroundChart />
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-primary sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-6 py-8">
            <div className="flex justify-between items-center">
              <img src={logo} alt="SinalInvest Logo" className="h-16" style={{ filter: 'brightness(0) invert(1)' }} />
              
              <nav className="hidden md:flex items-center gap-8 text-base font-medium text-gray-200">
                <a href="#features" className="hover:text-white transition-colors">Funcionalidades</a>
                <a href="#how-it-works" className="hover:text-white transition-colors">Como Funciona</a>
                <a href="#testimonials" className="hover:text-white transition-colors">Depoimentos</a>
                <a href="#pricing" className="hover:text-white transition-colors">Preços</a>
              </nav>
              
              <div className="hidden md:flex items-center gap-6">
                <button onClick={handleLoginClick} className="font-semibold text-gray-200 hover:text-white transition-all">
                  Entrar
                </button>
                <button onClick={handleSignupClick} className="px-6 py-2.5 rounded-lg font-bold text-dark bg-secondary hover:opacity-90 transition-all shadow-md">
                  Começar Grátis
                </button>
              </div>
              <div className="md:hidden flex items-center gap-3">
                <button onClick={handleLoginClick} className="text-sm font-semibold text-gray-200 hover:text-white transition-all">
                  Entrar
                </button>
                <button onClick={handleSignupClick} className="px-4 py-2 rounded-lg font-bold text-dark bg-secondary hover:opacity-90 transition-all shadow-md text-sm">
                  Começar
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <main>
          <section className="min-h-screen flex items-center pt-16 pb-20">
            <div className="container mx-auto px-6 text-center">
              <h1 className="text-4xl md:text-6xl font-extrabold text-dark leading-tight mb-6">
                Milhões de dados analisados. <br /> Uma visão <span className="text-primary">clara</span>.
              </h1>
              <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-600 mb-10">
                Estruturamos o caos do mercado. Entregamos análises de impacto fundamentadas em milhões de dados, 
                via Dashboard, Telegram e E-mail. Transforme Big Data em inteligência de investimento.
              </p>
              <div className="flex justify-center mb-16 px-4">
                <button onClick={handleSignupClick} className="px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg text-white bg-primary hover:scale-105 transition-transform shadow-lg">
                  Começar Grátis
                </button>
              </div>
              
              {/* Seção de Destaque */}
              <section className="py-4 md:py-6 mb-8 md:mb-10 border-y border-gray-200">
                <div className="max-w-4xl mx-auto px-4 text-center">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-dark mb-3">
                    Notícias relevantes, no momento certo
                  </h2>
                  <p className="text-base md:text-lg text-gray-600">
                    Receba notícias que podem impactar suas ações em tempo real, analisadas pela nossa IA para acelerar sua leitura de cenário.
                  </p>
                </div>
              </section>

              <div className="max-w-5xl mx-auto mt-12">
                <img
                  src={dashboardImage}
                  alt="Prévia do dashboard SinalInvest"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>
          </section>

          {/* Features Section - Zig Zag Layout */}
          <section id="features" className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl font-extrabold text-dark mb-4">Uma nova forma de ler o mercado</h2>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">Ferramentas desenhadas para te dar a vantagem que você precisa, com a simplicidade que você quer.</p>
              </div>
            </div>
          </section>

          {/* Bloco 1: A "Refinaria" de Dados */}
          <section className="py-24">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 md:order-1">
                  <video 
                    src={analyticsVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded-xl shadow-2xl w-full"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-dark mb-4 sm:mb-6">Não é mágica. É processamento massivo.</h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    Enquanto o mercado lê manchetes, nossa IA processa milhões de datapoints. Correlações ocultas, análise de sentimento institucional e impacto real no setor, tudo visualizado em um dashboard profissional.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Bloco 2: Inteligência no Bolso */}
          <section className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 md:order-1">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-dark mb-4 sm:mb-6">O sinal chega antes do ruído.</h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    Esqueça o spam. Receba no Telegram apenas o 'Filé Mignon': alertas críticos quando a tese de investimento de uma empresa muda. Para detalhes profundos, nossos relatórios de e-mail chegam na hora certa.
                  </p>
                </div>
                <div className="order-1 md:order-2 flex justify-center items-center">
                  <video 
                    src={telegramBotVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded-xl shadow-2xl w-full max-w-[280px] sm:max-w-[320px] md:max-w-sm h-auto"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Bloco 3: Busca Contextual */}
          <section className="py-24">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 md:order-1">
                  <video 
                    src={buscaPremiumVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded-xl shadow-2xl w-full"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-dark mb-4 sm:mb-6">Pergunte ao Mercado. Obtenha Respostas.</h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    Não pesquise por palavras-chave. Pesquise por contexto. Pergunte 'Como a alta do petróleo impacta a margem da Empresa X?' e nossa IA varre terabytes de dados para te dar a resposta estruturada.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How it Works Section */}
          <section id="how-it-works" className="py-24 bg-gradient-to-b from-white to-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-extrabold text-dark mb-4">Como Funciona</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">Em poucos passos, você começa a receber análises inteligentes de mercado</p>
              </div>

              <div className="max-w-5xl mx-auto">
                {/* Step 1: Criar Conta */}
                <div className="mb-16">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-bold shadow-lg">
                        1
                      </div>
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-2xl font-bold text-dark mb-4">Crie sua conta gratuitamente</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Cadastre-se em menos de 2 minutos. Não pedimos cartão de crédito. Você terá acesso imediato ao dashboard e poderá explorar todas as funcionalidades.
                      </p>
                      <div className="mt-6">
                        <button onClick={handleSignupClick} className="px-6 py-3 rounded-lg font-semibold text-white bg-primary hover:bg-blue-800 transition-all shadow-md">
                          Criar Conta Grátis
                        </button>
                      </div>
                    </div>
                    <div className="hidden md:block flex-shrink-0">
                      <img 
                        src="https://placehold.co/300x200/1e293b/ffffff?text=Criar+Conta" 
                        alt="Criar Conta" 
                        className="rounded-xl shadow-xl w-64"
                      />
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center mb-16">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>

                {/* Step 2: Escolher Plano */}
                <div className="mb-16">
                  <div className="flex flex-col md:flex-row-reverse items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-secondary text-dark flex items-center justify-center text-3xl font-bold shadow-lg">
                        2
                      </div>
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-2xl font-bold text-dark mb-4">Escolha o plano ideal</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Selecione o plano que melhor se adapta ao seu perfil de investimento. Você pode mudar ou cancelar a qualquer momento, sem complicações.
                      </p>
                      <div className="mt-6">
                        <a href="#pricing" className="inline-block px-6 py-3 rounded-lg font-semibold text-primary bg-white border-2 border-primary hover:bg-primary hover:text-white transition-all shadow-md">
                          Ver Planos
                        </a>
                      </div>
                    </div>
                    <div className="hidden md:block flex-shrink-0">
                      <img 
                        src="https://placehold.co/300x200/1e293b/ffffff?text=Escolher+Plano" 
                        alt="Escolher Plano" 
                        className="rounded-xl shadow-xl w-64"
                      />
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center mb-16">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>

                {/* Step 3: Receber Análises */}
                <div className="mb-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-bold shadow-lg">
                        3
                      </div>
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-2xl font-bold text-dark mb-4">Comece a receber análises inteligentes</h3>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        A partir daí, você receberá análises estruturadas através de múltiplos canais:
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Telegram */}
                        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">Telegram</h4>
                          </div>
                          <p className="text-sm text-gray-600">Alertas instantâneos no seu celular quando algo relevante acontece com suas empresas.</p>
                        </div>

                        {/* Email */}
                        <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">E-mail</h4>
                          </div>
                          <p className="text-sm text-gray-600">Relatórios detalhados e análises profundas entregues diretamente na sua caixa de entrada.</p>
                        </div>

                        {/* Dashboard */}
                        <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">Dashboard</h4>
                          </div>
                          <p className="text-sm text-gray-600">Visualize gráficos, tendências e insights em tempo real em um painel completo e interativo.</p>
                        </div>

                        {/* Relatórios */}
                        <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-orange-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">Relatórios</h4>
                          </div>
                          <p className="text-sm text-gray-600">Análises estruturadas e documentadas com impacto, contexto e recomendações estratégicas.</p>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block flex-shrink-0">
                      <img 
                        src="https://placehold.co/300x200/1e293b/ffffff?text=Receber+Analises" 
                        alt="Receber Análises" 
                        className="rounded-xl shadow-xl w-64"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Final */}
              <div className="text-center mt-16">
                <div className="bg-primary rounded-2xl p-8 text-white max-w-3xl mx-auto shadow-xl">
                  <h3 className="text-3xl font-bold mb-4">Pronto para começar?</h3>
                  <p className="text-lg opacity-90 mb-6">Junte-se a milhares de investidores que já transformam Big Data em decisões inteligentes.</p>
                  <button onClick={handleSignupClick} className="px-8 py-4 rounded-lg font-bold text-primary bg-white hover:scale-105 transition-transform shadow-lg">
                    Começar Agora - Grátis
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Comparativo Section */}
          <section className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-extrabold text-dark mb-4">A diferença é clara</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">Veja como investidores com SinalInvest tomam decisões mais inteligentes</p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-700 mb-6">O Investidor Comum</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Lê notícias atrasadas e reage emocionalmente</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Depende de fontes limitadas e análises superficiais</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Perde oportunidades por falta de contexto</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Decisões baseadas em intuição, não em dados</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-primary/5 rounded-2xl p-8 border-2 border-primary">
                  <h3 className="text-2xl font-bold text-primary mb-6">O Investidor SinalInvest</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Recebe dados estruturados e age com frieza</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Acessa milhões de datapoints em tempo real</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Antecipa movimentos com análises contextuais</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Decisões fundamentadas em Big Data e IA</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Free Trial Section */}
          <section className="py-20 bg-gradient-to-br from-primary via-blue-700 to-primary">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
                  <div className="inline-block bg-white/20 rounded-full px-6 py-2 mb-6">
                    <span className="text-white font-semibold text-sm md:text-base uppercase tracking-wider">Oferta Especial</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
                    7 Dias Grátis para Testar
                  </h2>
                  <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto">
                    Experimente todas as funcionalidades da plataforma por <strong>7 dias completamente grátis</strong>. 
                    Sem compromisso. Cancele quando quiser, sem perguntas.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                    <div className="flex items-center gap-3 text-white">
                      <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base md:text-lg">Acesso completo à plataforma</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base md:text-lg">Cancele quando quiser</span>
                    </div>
                  </div>
                  <button 
                    onClick={handleSignupClick}
                    className="px-10 py-4 rounded-lg font-bold text-lg text-primary bg-white hover:scale-105 transition-transform shadow-xl hover:shadow-2xl"
                  >
                    Começar Teste Grátis Agora
                  </button>
                  <p className="text-white/80 text-sm mt-6">
                    Cancele a qualquer momento durante o período de teste.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="pricing" className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-extrabold text-dark mb-4">Planos de Assinatura</h2>
                <p className="text-xl text-gray-600">Escolha o plano ideal para seus investimentos e mude a qualquer momento.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto">
                {[
                  {
                    name: 'Básico',
                    isPopular: false,
                    features: [
                      'Análise de até 5 empresas',
                      'Alertas via Telegram',
                      'Relatórios via email',
                      'Dashboard com visão geral',
                      'Suporte por email',
                    ],
                    price: '19,90',
                    period: 'mês',
                  },
                  {
                    name: 'Premium',
                    isPopular: true,
                    features: [
                      'Análise de até 50 empresas',
                      'Alertas via Telegram',
                      'Relatórios via email',
                      'Dashboard completo com insights',
                      'Suporte por email',
                    ],
                    price: '29,90',
                    period: 'mês',
                  },
                  {
                    name: 'Pro',
                    isPopular: false,
                    features: [
                      'Análise de empresas ilimitadas',
                      'Alertas via Telegram',
                      'Relatórios via email',
                      'Dashboard Pro com IA',
                      'Suporte dedicado 24/7',
                      'Acesso antecipado a novas features',
                    ],
                    price: '49,90',
                    period: 'mês',
                  },
                ].map((plan) => (
                  <div
                    key={plan.name}
                    className="flex flex-col bg-white rounded-2xl border border-gray-200 relative transition-all duration-300 overflow-hidden hover:-translate-y-1 hover:shadow-lg"
                  >
                    {plan.isPopular && (
                      <div className="absolute top-5 right-5 bg-blue-700 text-white px-4 py-2 rounded-full text-xs font-semibold shadow-lg z-10">
                        Recomendado para Gestão Ativa
                      </div>
                    )}
                    
                    <div className="flex flex-col flex-grow">
                      <div className="px-8 pt-8 pb-6 border-b border-gray-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <p className="text-sm text-gray-500">Planos pensados para cada perfil de investidor.</p>
                        <div className="mt-5">
                          <span className="text-3xl font-extrabold text-blue-700">R$ {plan.price}</span>
                          <span className="text-sm text-gray-500 ml-2">/ {plan.period}</span>
                        </div>
                      </div>

                      <div className="px-8 py-6 flex-grow">
                        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Benefícios</p>
                        <ul className="list-none p-0 flex-grow space-y-3">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3 text-gray-900">
                              <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="px-8 pb-8">
                        <button
                          type="button"
                          onClick={handleSignupClick}
                          className="w-full p-3 text-sm font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 transition-all duration-200"
                        >
                          Começar agora
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-primary text-white">
            <div className="container mx-auto px-6 py-20 text-center">
              <h2 className="text-4xl font-extrabold mb-4">Pronto para investir com mais segurança?</h2>
              <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">Junte-se a milhares de investidores que tomam decisões mais rápidas e inteligentes.</p>
              <button onClick={handleSignupClick} className="px-10 py-4 rounded-lg font-bold text-primary bg-white hover:scale-105 transition-transform shadow-lg">
                Começar meu teste grátis
              </button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-dark text-white">
          <div className="container mx-auto px-6 py-12">
            <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
               <div className="mb-8 md:mb-0">
                 <img src={logo} alt="SinalInvest Logo" className="h-10 mb-4 mx-auto md:mx-0" style={{ filter: 'brightness(0) invert(1)' }}/>
                 <p className="text-gray-400">Notícias viram lucro.</p>
               </div>
              <div className="flex gap-6 font-medium text-gray-300">
                <a href="#features" className="hover:text-secondary transition-colors">Funcionalidades</a>
                <a href="#pricing" className="hover:text-secondary transition-colors">Preços</a>
                <Link to="/termos-de-uso" className="hover:text-secondary transition-colors">Termos</Link>
                <Link to="/politica-de-privacidade" className="hover:text-secondary transition-colors">Privacidade</Link>
              </div>
            </div>
            <div className="text-center text-gray-500 mt-10 pt-8 border-t border-gray-700">
              <p>&copy; {new Date().getFullYear()} SinalInvest. Todos os direitos reservados.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;